import { BaseComponent } from './BaseComponent.js';
import { toggleFavorite, isFavorite, getCocktailId } from '../cocktail-state.js';
import { COCKTAIL_EVENTS } from '../constants.js';
import { ContentProcessor, AsyncErrorHandler } from '../utilities.js';
import { Log } from '../logger.js';
import { Validator } from '../validation.js';

class CocktailModal extends BaseComponent {
  constructor() {
    super('CocktailModal');
    this._cocktail = null;
    this._isOpen = false;
    this._previouslyFocusedElement = null;
    this._drinkId = null;
  }

  _initialize() {
    this.setupEventListeners();
    this.render();
  }

  setupEventListeners() {
    // Listen for modal open events
    this.addManagedEventListener(document, COCKTAIL_EVENTS.OPEN_MODAL, this.handleOpenModal);
    
    // Close modal on escape key
    this.addManagedEventListener(document, 'keydown', this.handleKeydown);
  }

  setupModalEventListeners() {
    // Add click listeners to modal content after render  
    this.addManagedEventListener(this, 'click', this.handleModalClick);
  }


  handleOpenModal = (event) => {
    this.openModal(event.detail.cocktail);
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this._isOpen) {
      this.closeModal();
    }
  }

  openModal(cocktail) {
    this._log.green('Opening modal for cocktail:', cocktail.name);
    
    // Store the element that was focused before opening modal
    this._previouslyFocusedElement = document.activeElement;
    
    this._cocktail = cocktail;
    this._isOpen = true;
    
    // Use the centralized ID function
    this._drinkId = getCocktailId(this._cocktail);
    
    this._log.blue('Modal opened with drink ID:', this._drinkId, 'for cocktail:', cocktail.name);
    
    this.render();
    this.setupReactiveFavorites();
    
    // Prevent body scrolling
    document.body.style.overflow = 'hidden';
    
    // Focus management and keyboard trap setup  
    this.addManagedTimeout(() => {
      this._setupFocusTrap();
      const closeButton = this.querySelector('.modal-close');
      if (closeButton) closeButton.focus();
    }, 100);
  }

  closeModal() {
    this._log.green('Closing modal');
    
    // Remove focus trap
    this._removeFocusTrap();
    
    this._isOpen = false;
    this._cocktail = null;
    this.render();
    
    // Restore body scrolling
    document.body.style.overflow = '';
    
    // Return focus to previously focused element
    if (this._previouslyFocusedElement) {
      this._previouslyFocusedElement.focus();
      this._previouslyFocusedElement = null;
    }
    
    // Dispatch event for router to update URL
    document.dispatchEvent(new CustomEvent('modal-closed'));
  }

  handleModalClick = (event) => {
    const closeButton = event.target.closest('.modal-close');
    if (closeButton) {
      event.preventDefault();
      this.closeModal();
      return;
    }

    const favoriteButton = event.target.closest('.modal-favorite-button');
    if (favoriteButton) {
      event.stopPropagation();
      this.toggleFavorite();
      return;
    }

    const copyButton = event.target.closest('.modal-copy-button');
    if (copyButton) {
      event.stopPropagation();
      this.copyToClipboard();
      return;
    }

    // Close modal if clicking on backdrop
    if (event.target.classList.contains('modal-backdrop')) {
      this.closeModal();
    }
  }

  toggleFavorite() {
    this._log.blue('Modal toggleFavorite called');
    this._log.blue('Cocktail:', this._cocktail);
    this._log.blue('Using drink ID:', this._drinkId);
    
    if (!this._cocktail || !this._drinkId) {
      this._log.yellow('No cocktail data or drink ID');
      return;
    }
    
    const isFavorited = toggleFavorite(this._drinkId);
    this._log.blue('Result:', isFavorited);
    
    // Show toast notification
    const message = isFavorited ? 'Added to favorites!' : 'Removed from favorites!';
    this.showToast(message);
    
    this._log.green('Toggled favorite for:', this._cocktail.name, 'ID:', this._drinkId, 'Now favorited:', isFavorited);
  }

  setupReactiveFavorites() {
    if (!this._cocktail || !this._drinkId) return;
    
    // Use managed effect for automatic cleanup
    this.addManagedEffect(() => {
      const isFavorited = isFavorite(this._drinkId);
      this.updateFavoriteButton(isFavorited);
    });
  }

  updateFavoriteButton(isFavorited) {
    const favoriteButton = this.querySelector('.modal-favorite-button');
    if (!favoriteButton) return;
    
    const svg = favoriteButton.querySelector('svg');
    if (svg) {
      svg.setAttribute('fill', isFavorited ? 'currentColor' : 'none');
    }
    
    favoriteButton.classList.toggle('favorited', isFavorited);
    favoriteButton.title = isFavorited ? 'Remove from favorites' : 'Add to favorites';
  }

  async copyToClipboard() {
    if (!this._cocktail) return;

    const text = `${this._cocktail.name}\n\nIngredients:\n${this._cocktail.ingredients || 'N/A'}\n\nInstructions:\n${this._cocktail.instructions || 'N/A'}`;
    
    const result = await AsyncErrorHandler.clipboard(
      text, 
      this, 
      'Cocktail copied to clipboard!'
    );
    
    if (result.success) {
      this._log.green('Cocktail copied to clipboard:', this._cocktail.name);
      
      // Dispatch event for stats tracking
      document.dispatchEvent(new CustomEvent('cocktail-copied', {
        detail: { cocktail: this._cocktail }
      }));
    }
  }

  showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  render() {
    if (!this._isOpen || !this._cocktail) {
      this.innerHTML = '';
      return;
    }

    const { name, category, glass, alcoholic, instructions, ingredients, image } = this._cocktail;
    
    // Use the consistent drink ID
    const isFavorited = this._drinkId ? isFavorite(this._drinkId) : false;
    
    // Process ingredients and instructions using centralized utilities with HTML escaping
    const ingredientsList = ContentProcessor.processIngredients(ingredients, Validator.escapeHtml);
    const instructionsList = ContentProcessor.processInstructions(instructions, Validator.escapeHtml);
    
    // Escape all user content using centralized escapeHtml
    const nameText = Validator.escapeHtml(name || 'Unnamed Cocktail');
    const categoryText = category ? Validator.escapeHtml(category) : '';
    const alcoholicText = alcoholic ? Validator.escapeHtml(alcoholic) : '';
    const glassText = glass ? Validator.escapeHtml(glass) : '';
    const imageAlt = Validator.escapeHtml(name || 'Cocktail image');

    this.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">${nameText}</h2>
            <button class="modal-close" type="button" aria-label="Close modal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <button class="modal-favorite-button ${isFavorited ? 'favorited' : ''}" type="button" title="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="${isFavorited ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
              </svg>
            </button>
            <button class="modal-copy-button" type="button" title="Copy cocktail">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
          
          <div class="modal-body">
            ${image ? `
              <div class="modal-image">
                <img src="${Validator.escapeHtml(image)}" alt="${imageAlt}" loading="lazy">
              </div>
            ` : ''}
            
            ${category || alcoholic || glass ? `
              <div class="modal-meta">
                ${category ? `<span class="meta-tag">${categoryText}</span>` : ''}
                ${alcoholic ? `<span class="meta-tag">${alcoholicText}</span>` : ''}
                ${glass ? `<span class="meta-tag">${glassText}</span>` : ''}
              </div>
            ` : ''}
            
            ${ingredientsList ? `
              <div class="modal-section">
                <h3>Ingredients:</h3>
                <ul class="ingredients-list">
                  ${ingredientsList}
                </ul>
              </div>
            ` : ''}
            
            ${instructionsList ? `
              <div class="modal-section">
                <h3>Instructions:</h3>
                <div class="instructions">
                  ${instructionsList}
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    // Re-attach event listeners after render
    this.setupModalEventListeners();
  }
  
  // Focus trap functionality for accessibility
  _setupFocusTrap() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    this._focusableElements = Array.from(focusableElements);
    this._firstFocusableElement = this._focusableElements[0];
    this._lastFocusableElement = this._focusableElements[this._focusableElements.length - 1];
    
    // Add keydown listener for tab trapping
    this._trapKeydownListener = this._handleTrapKeydown.bind(this);
    document.addEventListener('keydown', this._trapKeydownListener);
  }
  
  _removeFocusTrap() {
    if (this._trapKeydownListener) {
      document.removeEventListener('keydown', this._trapKeydownListener);
      this._trapKeydownListener = null;
    }
  }
  
  _handleTrapKeydown(event) {
    if (!this._isOpen || event.key !== 'Tab') return;
    
    if (event.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === this._firstFocusableElement) {
        event.preventDefault();
        this._lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === this._lastFocusableElement) {
        event.preventDefault();
        this._firstFocusableElement.focus();
      }
    }
  }
}

customElements.define('cocktail-modal', CocktailModal);
